import React, { useCallback, useEffect, useRef, useState } from 'react';
import cn from 'classnames';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import Card from '../Card';
import EmptyState from '../EmptyState';
import Button from '../../../../Button';
import Loader from '../../../../common/Loader';
import { useLazyLoad } from '../../../../../hocs/useLazyLoad';
import { MEDICATION_STATUSES } from '../../../../../constants/medicationStatuses';
import * as medicationsActions from '../../../../../redux/healthboard/patientPortal/medications-req';
import {
  selectMedicationsTotal,
  selectMedicationsEntity,
  selectMedicationsLoading,
} from '../../../../../redux/healthboard/patientPortal/selectors';

import styles from './index.styl';

const { ACTIVE, INACTIVE } = MEDICATION_STATUSES;

const MedicationsList = ({ userId, category, activeTab, formatMessage }) => {
  const [isVisible, setIsVisible] = useState(false);
  const rootRef = useRef();
  const dispatch = useDispatch();
  const total = useSelector(selectMedicationsTotal);
  const loading = useSelector(selectMedicationsLoading);
  const { active, discontinued } = useSelector(selectMedicationsEntity);

  const fetchData = withPagination => {
    const limit = 20;
    const offset = withPagination ? discontinued.length : 0;

    if (!withPagination) {
      dispatch(
        medicationsActions.actions.send({
          userId,
          status: ACTIVE,
        }),
      );
    }

    dispatch(
      medicationsActions.actions.send({
        limit,
        offset,
        userId,
        status: INACTIVE,
      }),
    );
  };

  const loadMore = useCallback(() => {
    const isLastPage = total <= discontinued.length;
    if (isLastPage) return;

    fetchData(true);
  }, [total, discontinued.length]);

  useEffect(() => {
    fetchData();

    return () => dispatch(medicationsActions.actions.clear());
  }, []);

  useLazyLoad({
    loadMore,
    node: rootRef,
    path: ['parentNode', 'parentNode'],
  });
  return (
    <div className={styles.medicationListWrapper} ref={rootRef}>
      {loading && active.length === 0 && discontinued.length === 0 && (
        <Loader />
      )}
      {!loading && active.length === 0 && discontinued.length === 0 && (
        <EmptyState
          category={category}
          activeTab={activeTab}
          formatMessage={formatMessage}
        />
      )}
      {active.length > 0
        ? active.map(card => (
            <Card
              key={card.id}
              entity={card}
              activeTab={activeTab}
              formatMessage={formatMessage}
            />
          ))
        : !isVisible &&
          discontinued.length > 0 && (
            <div>
              <EmptyState
                withInfo
                category={category}
                activeTab={activeTab}
                formatMessage={formatMessage}
              />
              <div className={cn(styles.showDiscontinued, styles.fullWidth)}>
                <Button
                  className={styles.buttonStyles}
                  onClick={() => setIsVisible(!isVisible)}
                >
                  {formatMessage({ id: 'common.showInactiveMedictions' })}
                </Button>
              </div>
            </div>
          )}

      {discontinued.length > 0 && !isVisible && active.length > 0 && (
        <div className={styles.showDiscontinued}>
          <Button
            className={styles.buttonStyles}
            onClick={() => setIsVisible(!isVisible)}
          >
            {formatMessage({ id: 'common.showInactiveMedictions' })}
          </Button>
        </div>
      )}

      <div className={styles.paginationRoot}>
        {isVisible &&
          discontinued.map(card => (
            <Card
              isInactive
              key={card.id}
              entity={card}
              activeTab={activeTab}
              formatMessage={formatMessage}
            />
          ))}
      </div>
    </div>
  );
};

MedicationsList.propTypes = {
  userId: PropTypes.number,
  category: PropTypes.string,
  activeTab: PropTypes.string,
  formatMessage: PropTypes.func,
};

export default MedicationsList;



// WEBPACK FOOTER //
// ./src/components/PatientHealthboard/PatientPortal/DataList/MedicationsList/index.jsx